import React, { useEffect, useState } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import { allAPiServicesCall } from "../../services/apiServices";
import { getAuthConfig, getFormDataConfig } from "../../services/apiUtils";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { extractObjectId } from "../../utils/utils";

function EditPlaylistModal({ show, handleClose, playlistDetails, onPlaylistUpdated }) {
  const navigate = useNavigate();

  const [updatedData, setUpdatedData] = useState({
    playlist_id: extractObjectId(playlistDetails._id),
    playlist_name: playlistDetails.playlist_name,
    playlist_description: playlistDetails.playlist_description,
    playlist_type: playlistDetails.playlist_type,
    playlist_image: playlistDetails.playlist_image,
    priority: playlistDetails.priority,
  });

  // Artist tagging state
  const [taggedArtists, setTaggedArtists] = useState([]);
  const [artistSearch, setArtistSearch] = useState('');
  const [artistResults, setArtistResults] = useState([]);
  const [showArtistDropdown, setShowArtistDropdown] = useState(false);

  useEffect(() => {
    if (playlistDetails) {
      setUpdatedData({ ...playlistDetails });
      // Initialize tagged artists from playlist data
      setTaggedArtists(playlistDetails.taggedArtists || []);
    }
  }, [playlistDetails]);

  const [newImageFile, setNewImageFile] = useState(null);
  const [imagePreview,setImagePreview] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUpdatedData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setNewImageFile(file);
    const objectUrl = URL.createObjectURL(file);
    setImagePreview(objectUrl)
  };

  // Artist search handler
  const handleArtistSearch = async (searchTerm) => {
    setArtistSearch(searchTerm);
    if (searchTerm.length < 2) {
      setArtistResults([]);
      setShowArtistDropdown(false);
      return;
    }

    try {
      const response = await allAPiServicesCall.getSearchArtist(
        { query: searchTerm },
        getAuthConfig(),
        navigate
      );
      if (response?.data) {
        setArtistResults(response.data);
        setShowArtistDropdown(true);
      }
    } catch (error) {
      console.error('Error searching artists:', error);
    }
  };

  const handleAddArtist = (artist) => {
    if (!taggedArtists.find(a => extractObjectId(a._id) === extractObjectId(artist._id))) {
      setTaggedArtists([...taggedArtists, artist]);
    }
    setArtistSearch('');
    setArtistResults([]);
    setShowArtistDropdown(false);
  };

  const handleRemoveArtist = (artistId) => {
    setTaggedArtists(taggedArtists.filter(a => extractObjectId(a._id) !== extractObjectId(artistId)));
  };

  const uploadImageAndGetUrl = async () => {
    if (!newImageFile) return null;
    try {
      const formData = new FormData();
      formData.append("image", newImageFile);

      const response = await allAPiServicesCall.uploadPlaylistImage(
        formData,
        getFormDataConfig()
      );
      return response.data.path;
    } catch (error) {
      console.error("Image upload failed:", error.response?.data || error);
      return null;
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    let imageUrl = updatedData.playlist_image;

    if (newImageFile) {
      const uploadedImageUrl = await uploadImageAndGetUrl();
      if (uploadedImageUrl) {
        imageUrl = uploadedImageUrl;
      }
    }

    const finalUpdatedData = { ...updatedData, playlist_image: imageUrl };

    const updatedataplaylistData = {
      ...finalUpdatedData,
      playlist_id: extractObjectId(playlistDetails._id),
      taggedArtists: taggedArtists.map(artist => extractObjectId(artist._id)), // Add tagged artists
    }
    try {
      const updateResponse = await allAPiServicesCall.patchUpdateAdminPlaylist(
        updatedataplaylistData,
        getAuthConfig()
      );

      if (updateResponse && updateResponse.success) {
        toast.success("Playlist updated successfully");
        if (onPlaylistUpdated) {
          // Pass the locally updated data with all fields
          onPlaylistUpdated({
            ...playlistDetails,
            ...finalUpdatedData,
            taggedArtists: taggedArtists,
          });
        }
        handleClose();
      } else {
        toast.error("Failed to update playlist");
      }
    } catch (error) {
      console.error("Failed to update playlist:", error.response?.data || error);
      toast.error("An error occurred while updating the playlist");
    }
  };

  const handleModalClose = () => {
    setTaggedArtists([]);
    setArtistSearch('');
    setArtistResults([]);
    setShowArtistDropdown(false);
    handleClose();
  };

  return (
    <Modal show={show} onHide={handleModalClose}>
      <Modal.Header closeButton>
        <Modal.Title>Edit Playlist</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={onSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Playlist Name</Form.Label>
            <Form.Control
              type="text"
              name="playlist_name"
              value={updatedData.playlist_name}
              onChange={handleInputChange}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              name="playlist_description"
              value={updatedData.playlist_description}
              onChange={handleInputChange}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Playlist Image</Form.Label>
            {updatedData.playlist_image && (
              <div className="mb-3">
                <img
                  src={imagePreview ||  updatedData.playlist_image}
                  alt="Playlist"
                  style={{ width: "100%", height: "auto", marginBottom: "10px" }}
                />
              </div>
            )}
            <Form.Control
              type="file"
              name="playlist_image"
              onChange={handleFileChange}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Playlist Type</Form.Label>
            <Form.Control
              as="select"
              name="playlist_type"
              value={updatedData.playlist_type}
              onChange={handleInputChange}
            >
              <option value="public">Public</option>
              <option value="private">Private</option>
            </Form.Control>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Playlist Priority</Form.Label>
            <Form.Control
              type="number"
              name="priority"
              value={updatedData.priority}
              onChange={handleInputChange}
              required
            />
          </Form.Group>

          {/* Artist Tagging Section */}
          <Form.Group className="mb-3">
            <Form.Label>Tag Artists</Form.Label>
            <div className="position-relative">
              <Form.Control
                type="text"
                value={artistSearch}
                onChange={(e) => handleArtistSearch(e.target.value)}
                placeholder="Search for artists to tag..."
              />
              {showArtistDropdown && artistResults.length > 0 && (
                <div className="position-absolute w-100 bg-white border rounded shadow-sm mt-1" style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}>
                  {artistResults.map((artist) => (
                    <div
                      key={extractObjectId(artist._id)}
                      className="p-2"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleAddArtist(artist)}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                    >
                      {artist.artist_name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {taggedArtists.length > 0 && (
              <div className="mt-2">
                <small className="text-muted d-block mb-1">Tagged Artists:</small>
                <div className="d-flex flex-wrap gap-2">
                  {taggedArtists.map((artist) => (
                    <span key={extractObjectId(artist._id)} className="badge bg-primary d-flex align-items-center gap-1">
                      {artist.artist_name}
                      <button
                        type="button"
                        className="btn-close btn-close-white"
                        style={{ fontSize: '0.6rem' }}
                        onClick={() => handleRemoveArtist(extractObjectId(artist._id))}
                        aria-label="Remove"
                      ></button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </Form.Group>

          <Button variant="primary" type="submit" className="float-end">
            Save Changes
          </Button>
          <Button variant="danger" onClick={handleModalClose}>
            Cancel
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default EditPlaylistModal;
